import { AbstractService } from '@uniqoders/sdk';
import Invoice from '@/api/objects/Invoice';

export default class InvoiceService extends AbstractService {
  /**
   * Retrieves all Invoices
   * @param agency
   * @param clientTour
   * @param tour
   * @protected
   */
  public all(
    agency: string,
    clientTour: number,
    tour: string | number): Promise<Invoice[]> {
    return this.request({
      method: 'get',
      url: this.buildPath('/{agency}/case/{clientTour}/tour/{tour}/invoices', { agency, clientTour, tour }),
    });
  }

  /**
   * Retrieves a specific Invoice
   * @param agency
   * @param clientTour
   * @param tour
   * @param invoice
   * @protected
   */
  public retrieve(
    agency: string,
    clientTour: number,
    tour: string | number,
    invoice: number): Promise<Invoice> {
    return this.request({
      method: 'get',
      url: this.buildPath('/{agency}/case/{clientTour}/tour/{tour}/invoice/{invoice}', {
        agency,
        clientTour,
        tour,
        invoice
      }),
    });
  }

  /**
   * Creates a single Invoice
   * @protected
   * @param agency
   * @param clientTour
   * @param tour
   * @param data
   */
  public create(
    agency: string,
    clientTour: number,
    tour: string | number,
    data: Record<string, any>): Promise<Invoice> {
    return this.request({
      method: 'post',
      url: this.buildPath('/{agency}/case/{clientTour}/tour/{tour}/invoice', { agency, clientTour, tour }),
      data,
    });
  }

  /**
   * Updates the specified Invoice
   * @param agency
   * @param clientTour
   * @param tour
   * @param invoice
   * @param data
   * @protected
   */
  public update(
    agency: string,
    clientTour: number,
    tour: string | number,
    invoice: number,
    data: Record<string, any>): Promise<Invoice> {
    return this.request({
      method: 'patch',
      url: this.buildPath('/{agency}/case/{clientTour}/tour/{tour}/invoice/{invoice}', {
        agency,
        clientTour,
        tour,
        invoice
      }),
      data,
    });
  }

  /**
   * Archives the specified Invoice
   * @protected
   * @param agency
   * @param clientTour
   * @param tour
   * @param invoice
   */
  public archive(
    agency: string,
    clientTour: number,
    tour: string | number,
    invoice: string | number): Promise<Invoice> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/{agency}/case/{clientTour}/tour/{tour}/invoice/{invoice}', {
        agency,
        clientTour,
        tour,
        invoice
      }),
    });
  }

  /**
   * Deletes the specified Invoice
   * @protected
   * @param agency
   * @param clientTour
   * @param tour
   * @param invoice
   */
  public delete(agency: string, clientTour: number, tour: string | number, invoice: number): Promise<Invoice> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/{agency}/case/{clientTour}/tour/{tour}/invoice/{invoice}/force', {
        agency,
        clientTour,
        tour,
        invoice
      }),
    });
  }

  /**
   * Restores the specified Invoice
   * @protected
   * @param agency
   * @param clientTour
   * @param tour
   * @param invoice
   */
  public restore(agency: string, clientTour: number, tour: string | number, invoice: number): Promise<Invoice> {
    return this.request({
      method: 'delete',
      url: this.buildPath('/{agency}/case/{clientTour}/tour/{tour}/invoice/{invoice}/force', {
        agency,
        clientTour,
        tour,
        invoice
      }),
    });
  }

  /**
   * Download the specified Invoice
   * @protected
   * @param agency
   * @param clientTour
   * @param tour
   * @param invoice
   */
  public download(agency: string, clientTour: number, tour: string | number, invoice: number): Promise<Invoice> {
    return this.request({
      method: 'get',
      url: this.buildPath('/{agency}/case/{clientTour}/tour/{tour}/invoice/{invoice}/download', {
        agency,
        clientTour,
        tour,
        invoice
      }),
    });
  }

    /**
   * Creates a  Invoice Summary
   * @protected
   * @param agency
   * @param clientTour
   * @param tour
   * @param data
   */
    public createInvoice(
      agency: string,
      clientTour: number,
      tour: string | number,
      data: Record<string, any>): Promise<Invoice> {
      return this.request({
        method: 'post',
        url: this.buildPath('/{agency}/case/{clientTour}/tour/{tour}/invoice/summary', { agency, clientTour, tour }),
        data,
      });
    }

     /**
   * Creates a  Invoice Summary
   * @protected
   * @param agency
   * @param clientTour
   * @param tour
   */
     public invoiceList(
      agency: string,
      clientTour: number,
      tour: string | number): Promise<Invoice> {
      return this.request({
        method: 'get',
        url: this.buildPath('/{agency}/case/{clientTour}/tour/{tour}/invoices', { agency, clientTour, tour }),
      });
    }

   /**
   * Creates a  Invoice Summary
   * @protected
   * @param agency
   * @param clientCase
   */
     public getInvoice(
      agency: string,
      clientCase: number): Promise<Invoice> {
      return this.request({
        method: 'get',
        url: this.buildPath('/v2/{agency}/case/{clientCase}/invoices', { agency, clientCase }),
        timeout: 30000,
      });
    }

    /**
   * Retrieves all Invoices
   * @param clientTour
   * @param tour
   * @param invoice
   * @param params
   * @protected
   */
  public invoicePdf(
    clientTour: number,
    tour: string | number,
    invoice: string | number,
    params: Record<string, any>): Promise<Invoice[]> {
    return this.request({
      method: 'get',
      url: this.buildPath('/case/{clientTour}/tour/{tour}/invoice/{invoice}/preview', { clientTour, tour, invoice }),
      params,
    });
  }

    /**
   * Retrieves all Invoices
   * @param clientTour
   * @param tour
   * @param invoice
   * @param params
   * @protected
   */
    public invoiceEmail(
      clientTour: number,
      tour: string | number,
      invoice: string | number,
      params: Record<string, any>): Promise<Invoice[]> {
      return this.request({
        method: 'get',
        url: this.buildPath('/case/{clientTour}/tour/{tour}/invoice/send-mail/{invoice}', { clientTour, tour, invoice }),
        params,
      });
    }

  /**
   * Retrieves all Invoices
   * @param clientTour
   * @param tour
   * @param invoice
   * @param params
   * @protected
   */
  public downloadInvoice(
    clientTour: number,
    tour: string | number,
    invoice: string | number,
    params: Record<string, any>,
    config: Record<string, any> = {}): Promise<Invoice[]> {

    return this.request({
      method: 'get',
      url: this.buildPath('/case/{clientTour}/tour/{tour}/invoice/{invoice}/download', { clientTour, tour, invoice }),
      params,
      ...config,
    });
  }

   /**
   * Confirm Invoice Summary
   * @protected
   * @param agency
   * @param clientTour
   * @param tour
   * @param data
   */
   public invoiceSummary(
    agency: string,
    clientTour: number,
    tour: string | number,
    data: Record<string, any>): Promise<Invoice> {
    return this.request({
      method: 'post',
      url: this.buildPath('/{agency}/case/{clientTour}/tour/{tour}/invoice/supplier/summary', { agency, clientTour, tour }),
      data,
    });
  }


  /**
   * Confirm Invoice Summary wise
   * @protected
   * @param agency
   * @param clientTour
   * @param tour
   * @param data
   */
  public invoiceSummaryWise(
    agency: string,
    clientTour: number,
    tour: string | number,
    data: Record<string, any>): Promise<Invoice> {
    return this.request({
      method: 'post',
      url: this.buildPath('/{agency}/case/{clientTour}/tour/{tour}/invoice/supplier/pay-via-wise', { agency, clientTour, tour }),
      data,
    });
  }

  /**
   * Reset the Holded connection flag for selected tour day items.
   */
  public resetHoldedConnection(
    agency: string,
    clientTour: number,
    tour: string | number,
    data: Record<string, any>): Promise<Invoice> {
    return this.request({
      method: 'post',
      url: this.buildPath('/{agency}/case/{clientTour}/tour/{tour}/invoice/reset-holded', { agency, clientTour, tour }),
      data,
    });
  }

    /**
   * Creates a  Invoice custom notes
   * @protected
   * @param agency
   * @param clientTour
   * @param tour
   * @param data
   */
    public invoiceCustomNotes(
      agency: string,
      clientTour: number,
      tour: string | number,
      data: Record<string, any>): Promise<Invoice> {
      return this.request({
        method: 'post',
        url: this.buildPath('/{agency}/case/{clientTour}/tour/{tour}/invoice/custom-notes', { agency, clientTour, tour }),
        data,
      });
    }

    /**
   * show a  Invoice Summary
   * @protected
   * @param agency
   * @param clientCase
   * @param invoiceId
   */
    public showInvoice(
      agency: string,
      clientCase: number,
      invoiceId: number): Promise<Invoice> {
      return this.request({
        method: 'get',
        url: this.buildPath('/v2/{agency}/case/{clientCase}/invoice-details/{invoiceId}', { agency, clientCase, invoiceId }),
      });
    }

   /**
   * send a Invoice Email
   * @protected
   * @param agency
   * @param clientCase
   * @param invoiceId
   * @param data
   */
     public sendInvoiceEmail(
      agency: string,
      clientCase: number,
      invoiceId: number,
      data: Record<string, any>): Promise<Invoice> {
      return this.request({
        method: 'post',
        url: this.buildPath('/v2/{agency}/case/{clientCase}/send-invoice/{invoiceId}', { agency, clientCase, invoiceId }),
        data
      });
    }

    /**
   * send a Invoice Email
   * @protected
   * @param agency
   * @param clientCase
   * @param invoiceId
   * @param data
   */
    public downloadCaseInvoice(
      agency: string,
      clientCase: number,
      invoiceId: number): Promise<Invoice> {
      return this.request({
        method: 'post',
        url: this.buildPath('/v2/{agency}/case/{clientCase}/download-invoice/{invoiceId}', { agency, clientCase, invoiceId }),
      });
    }
}
